import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { reviewsData, reviewLinks } from '../data/mock';
import { motion } from 'framer-motion';
import { Star, Quote, ExternalLink, Shield } from 'lucide-react';

const StarRating = ({ rating, size = 'sm' }) => {
  const sizeClass = size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <svg
          key={i}
          className={`${sizeClass} ${i < rating ? 'text-amber-400 fill-current' : 'text-gray-200 fill-current'}`}
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
};

const ReviewCard = ({ review, index, language }) => (
  <motion.div
    initial={{ opacity: 0, y: 25 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    className="group relative p-6 bg-white rounded-2xl border border-[#E2E8F0] hover:border-[#0D9488]/20 hover:shadow-xl hover:shadow-[#0D9488]/5 transition-all duration-500"
  >
    <Quote className="absolute top-5 right-5 w-8 h-8 text-[#0D9488]/8 group-hover:text-[#0D9488]/15 transition-colors duration-500" />
    <div className="flex items-center gap-3 mb-4">
      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#0D9488] to-[#0C4A6E] flex items-center justify-center text-white text-sm font-bold">
        {review.avatar}
      </div>
      <div>
        <h4 className="font-semibold text-[#0C4A6E] text-sm">{review.name}</h4>
        <div className="flex items-center gap-2">
          <StarRating rating={review.rating} />
          <span className="text-xs text-[#94A3B8] font-medium">
            {review.source === 'google' ? 'Google' : 'TripAdvisor'}
          </span>
        </div>
      </div>
    </div>
    <p className="text-[#475569] text-sm leading-relaxed">
      {review.text[language]}
    </p>
  </motion.div>
);

/* Google "G" logo SVG */
const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

/* TripAdvisor owl logo SVG */
const TripAdvisorIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 4.48 2 7.5c0 1.41.84 2.69 2.21 3.71L2 15l3.79-1.52C7.6 14.43 9.72 15 12 15s4.4-.57 6.21-1.52L22 15l-2.21-3.79C21.16 10.19 22 8.91 22 7.5 22 4.48 17.52 2 12 2zm-3.5 9a2.5 2.5 0 110-5 2.5 2.5 0 010 5zm7 0a2.5 2.5 0 110-5 2.5 2.5 0 010 5z"/>
  </svg>
);

export const Reviews = () => {
  const { language, t } = useLanguage();

  return (
    <section id="reviews" className="relative py-28 lg:py-36 bg-[#FAFAF8] overflow-hidden">
      {/* Subtle background accents */}
      <div className="absolute top-20 left-0 w-72 h-72 bg-[#0D9488]/3 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-0 w-80 h-80 bg-[#F0FDFA] rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-2xl mx-auto text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-semibold text-[#0D9488] tracking-[0.2em] uppercase mb-4">
              {t('reviews.tagline')}
            </span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#0C4A6E] mb-4">
              {t('reviews.title')}
            </h2>
            <p className="text-lg text-[#64748B]">
              {t('reviews.subtitle')}
            </p>
          </motion.div>
        </div>

        {/* Platform Rating Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto mb-12"
        >
          {/* Google Reviews Card */}
          <a
            href={reviewLinks.google}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative flex items-center gap-5 p-6 bg-white rounded-2xl border border-[#E2E8F0] hover:border-[#4285F4]/30 hover:shadow-xl hover:shadow-[#4285F4]/5 transition-all duration-500"
          >
            <div className="w-14 h-14 rounded-2xl bg-[#F8F9FA] flex items-center justify-center flex-shrink-0 group-hover:bg-[#4285F4]/5 transition-colors duration-300">
              <GoogleIcon className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-[#334155] text-base mb-1">{t('reviews.googleReviews')}</p>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="font-display text-2xl font-bold text-[#0C4A6E]">4.9</span>
                <StarRating rating={5} size="lg" />
              </div>
              <p className="text-xs text-[#94A3B8]">{t('reviews.basedOn')} 187+ {t('reviews.reviewsText')}</p>
            </div>
            <ExternalLink className="w-4 h-4 text-[#94A3B8] group-hover:text-[#4285F4] transition-colors flex-shrink-0" />
          </a>

          {/* TripAdvisor Card */}
          <a
            href={reviewLinks.tripadvisor}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative flex items-center gap-5 p-6 bg-white rounded-2xl border border-[#E2E8F0] hover:border-[#00AF87]/30 hover:shadow-xl hover:shadow-[#00AF87]/5 transition-all duration-500"
          >
            <div className="w-14 h-14 rounded-2xl bg-[#F0FDF9] flex items-center justify-center flex-shrink-0 group-hover:bg-[#00AF87]/10 transition-colors duration-300">
              <TripAdvisorIcon className="w-7 h-7 text-[#00AF87]" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-[#334155] text-base mb-1">{t('reviews.tripAdvisor')}</p>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="font-display text-2xl font-bold text-[#0C4A6E]">4.8</span>
                <StarRating rating={5} size="lg" />
              </div>
              <p className="text-xs text-[#94A3B8]">{t('reviews.basedOn')} 124+ {t('reviews.reviewsText')}</p>
            </div>
            <ExternalLink className="w-4 h-4 text-[#94A3B8] group-hover:text-[#00AF87] transition-colors flex-shrink-0" />
          </a>
        </motion.div>

        {/* Verified Badge */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-center gap-2 mb-14"
        >
          <Shield className="w-4 h-4 text-[#0D9488]" />
          <span className="text-sm text-[#64748B]">{t('reviews.trustSubtitle')}</span>
        </motion.div>

        {/* Review Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
          {reviewsData.map((review, index) => (
            <ReviewCard
              key={review.id}
              review={review}
              index={index}
              language={language}
            />
          ))}
        </div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href={reviewLinks.google}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-white border-2 border-[#E2E8F0] rounded-xl font-semibold text-[#334155] hover:border-[#4285F4] hover:text-[#4285F4] hover:shadow-lg hover:shadow-[#4285F4]/10 transition-all duration-300"
          >
            <GoogleIcon className="w-5 h-5" />
            {t('reviews.readGoogle')}
            <ExternalLink className="w-4 h-4 opacity-50 group-hover:opacity-100 transition-opacity" />
          </a>
          <a
            href={reviewLinks.tripadvisor}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-white border-2 border-[#E2E8F0] rounded-xl font-semibold text-[#334155] hover:border-[#00AF87] hover:text-[#00AF87] hover:shadow-lg hover:shadow-[#00AF87]/10 transition-all duration-300"
          >
            <TripAdvisorIcon className="w-5 h-5 text-[#00AF87]" />
            {t('reviews.readTripadvisor')}
            <ExternalLink className="w-4 h-4 opacity-50 group-hover:opacity-100 transition-opacity" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
